import { useEffect, useState } from 'react';

import { useMirrorPermissions } from '@/hooks/useMirrorPermissions';
import { FACILITY_USER_ACCESS_MODE } from '@/constants/facilityUserAccessModes';
import { validateFacilityUserAccessSelection } from './facilityUserFormValidation.utils';
import { toPermissionPayload } from './facilityPermissionsPayload.utils';

const INITIAL_FORM_DATA = {
  email: '',
  userName: '',
  mfa_type: 'NONE',
  languagePreference: 'en',
};

const removeErrors = (errors, errorKeys) => {
  const nextErrors = { ...errors };
  errorKeys.forEach((errorKey) => delete nextErrors[errorKey]);
  return nextErrors;
};

export const useUserFormDialog = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] = useState({});
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [facilityPermissions, setFacilityPermissions] = useState({});
  const [organizationPermissions, setOrganizationPermissions] = useState({});
  const [applyToAllFacilities, setApplyToAllFacilities] = useState(true);
  const [selectedFacilityForConfig, setSelectedFacilityForConfig] = useState(
    '',
  );
  const [permissionsType, setPermissionsType] = useState('permission');
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [accessMode, setAccessMode] = useState(
    FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES,
  );
  const [selectedFacilityGroupIds, setSelectedFacilityGroupIds] = useState([]);
  const [roleBasedFacilitiesAccess, setRoleBasedFacilitiesAccess] = useState(
    false,
  );

  const handleAccessModeChange = (newMode) => {
    setAccessMode(newMode);
    if (newMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES) {
      setApplyToAllFacilities(true);
      setSelectedFacilityForConfig('');
    }
    setFormErrors((prev) => {
      const errorKeys = ['accessMode'];

      if (newMode !== FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES) {
        errorKeys.push('facilities');
      }

      if (newMode !== FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS) {
        errorKeys.push('facility_groups');
      }

      return removeErrors(prev, errorKeys);
    });

    if (newMode !== FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES) {
      setSelectedFacilities([]);
    }

    if (newMode !== FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS) {
      setSelectedFacilityGroupIds([]);
    }
  };

  const handleApplyMirroredPermissions = (
    mirroredPermissions,
    sourceUser,
    permissionType,
  ) => {
    if (permissionType === 'organization_permissions') {
      setOrganizationPermissions(mirroredPermissions);
    } else if (permissionType === 'facility_permissions') {
      if (
        accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES ||
        applyToAllFacilities
      ) {
        setFacilityPermissions(mirroredPermissions);
      } else if (selectedFacilityForConfig) {
        setFacilityPermissions((prev) => ({
          ...prev,
          [selectedFacilityForConfig]: mirroredPermissions,
        }));
      }
    }
  };

  const {
    mirrorDialogOpen,
    mirrorPermissionType,
    openMirrorDialog,
    closeMirrorDialog,
    applyMirroredPermissions,
  } = useMirrorPermissions(handleApplyMirroredPermissions);

  useEffect(() => {
    if (open) {
      setFormData(INITIAL_FORM_DATA);
      setSelectedFacilities([]);
      setSelectedOrganization('');
      setFacilityPermissions({});
      setOrganizationPermissions({});
      setApplyToAllFacilities(true);
      setSelectedFacilityForConfig('');
      setPermissionsType('permission');
      setSelectedRoleId(null);
      setFormErrors({});
      setAccessMode(FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES);
      setSelectedFacilityGroupIds([]);
      setRoleBasedFacilitiesAccess(false);
    }
  }, [open]);

  useEffect(() => {
    if (
      accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES &&
      selectedFacilities.length > 0 &&
      formErrors.facilities
    ) {
      setFormErrors((prev) => removeErrors(prev, ['facilities']));
    }

    if (
      accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS &&
      selectedFacilityGroupIds.length > 0 &&
      formErrors.facility_groups
    ) {
      setFormErrors((prev) => removeErrors(prev, ['facility_groups']));
    }

    if (
      accessMode !== FACILITY_USER_ACCESS_MODE.NONE &&
      formErrors.accessMode
    ) {
      setFormErrors((prev) => removeErrors(prev, ['accessMode']));
    }

    if (permissionsType === 'role' && selectedRoleId && formErrors.role) {
      setFormErrors((prev) => removeErrors(prev, ['role']));
    }
  }, [
    accessMode,
    formErrors.accessMode,
    formErrors.facilities,
    formErrors.facility_groups,
    formErrors.role,
    permissionsType,
    selectedRoleId,
    selectedFacilities,
    selectedFacilityGroupIds,
  ]);

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (formErrors[fieldName]) {
      setFormErrors((prev) => removeErrors(prev, [fieldName]));
    }
  };

  const handleFacilityPermissionChange = (newPermissions) => {
    if (
      accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES ||
      applyToAllFacilities
    ) {
      setFacilityPermissions(newPermissions);
    } else {
      const facilityId = selectedFacilityForConfig;
      setFacilityPermissions((prev) => ({
        ...prev,
        [facilityId]: newPermissions,
      }));
    }
  };

  const handleOrganizationPermissionChange = (newPermissions) => {
    setOrganizationPermissions(newPermissions);
  };

  const handlePermissionsTypeChange = (newType) => {
    setPermissionsType(newType);
    if (newType === 'role') {
      setFacilityPermissions({});
      setOrganizationPermissions({});
    } else {
      setSelectedRoleId(null);
      setRoleBasedFacilitiesAccess(false);
    }
  };

  const handleRoleChange = (roleId, hasRoleBasedFacilitiesAccess) => {
    const previousRoleGrantedAllFacilitiesAccess = roleBasedFacilitiesAccess;

    setSelectedRoleId(roleId);
    setRoleBasedFacilitiesAccess(hasRoleBasedFacilitiesAccess);
    setFormErrors((prev) => removeErrors(prev, ['role']));

    if (hasRoleBasedFacilitiesAccess) {
      setAccessMode(FACILITY_USER_ACCESS_MODE.ALL_FACILITIES);
      setSelectedFacilities([]);
      setSelectedFacilityGroupIds([]);
      return;
    }

    if (previousRoleGrantedAllFacilitiesAccess) {
      setAccessMode(FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES);
      setSelectedFacilities([]);
      setSelectedFacilityGroupIds([]);
    }
  };

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganization(organizationId);
    setSelectedFacilities([]);
    setSelectedFacilityGroupIds([]);
    setSelectedRoleId(null);
    setRoleBasedFacilitiesAccess(false);
    setAccessMode(FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES);

    if (organizationId) {
      setOrganizationPermissions({});
    }
  };

  const handleApplyTemplate = (template) => {
    if (template.organizationPermissions) {
      setOrganizationPermissions(template.organizationPermissions);
    }

    const templateFacilityPerms =
      template.facilityPermissions || template.permissions;

    if (templateFacilityPerms) {
      if (
        accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES ||
        applyToAllFacilities
      ) {
        setFacilityPermissions(templateFacilityPerms);
      } else if (selectedFacilityForConfig) {
        setFacilityPermissions((prev) => ({
          ...prev,
          [selectedFacilityForConfig]: templateFacilityPerms,
        }));
      }
    }
  };

  const handleApplyToAllFacilitiesChange = (newApplyToAll) => {
    setApplyToAllFacilities(newApplyToAll);
    setFacilityPermissions({});
  };

  useEffect(() => {
    if (
      !applyToAllFacilities &&
      selectedFacilities.length > 0 &&
      !selectedFacilityForConfig
    ) {
      const firstFacilityId = selectedFacilities[0];
      setSelectedFacilityForConfig(firstFacilityId);
    }
  }, [applyToAllFacilities, selectedFacilities, selectedFacilityForConfig]);

  const getConfiguredFacilitiesCount = () => {
    if (applyToAllFacilities) {
      return facilityPermissions && Object.keys(facilityPermissions).length > 0
        ? selectedFacilities.length
        : 0;
    } else {
      return selectedFacilities.filter((facilityId) => {
        return (
          facilityPermissions[facilityId] &&
          Object.keys(facilityPermissions[facilityId]).length > 0
        );
      }).length;
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.userName?.trim()) {
      errors.userName = 'Name is required';
    }

    Object.assign(
      errors,
      validateFacilityUserAccessSelection({
        permissionsType,
        selectedRoleId,
        selectedOrganization,
        accessMode,
        selectedFacilities,
        selectedFacilityGroupIds,
      }),
    );

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const userData = {
        email: formData.email,
        name: formData.userName,
        mfaType: formData.mfa_type,
        languagePreference: formData.languagePreference,
        facilityIds:
          accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES
            ? selectedFacilities.map((facilityId) => parseInt(facilityId))
            : [],
        facilityGroupIds:
          accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS
            ? selectedFacilityGroupIds.map((facilityGroupId) =>
                parseInt(facilityGroupId),
              )
            : [],
        accessMode,
        organizationId: selectedOrganization
          ? parseInt(selectedOrganization)
          : null,
        permissionsType: permissionsType,
        hasAllFacilitiesAccess:
          accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES,
      };

      if (permissionsType === 'role') {
        userData.roleId = selectedRoleId;
      } else {
        const permissionsArray = [];

        if (
          selectedOrganization &&
          organizationPermissions &&
          Object.keys(organizationPermissions).length > 0
        ) {
          permissionsArray.push({
            organizationId: parseInt(selectedOrganization),
            permissions: organizationPermissions,
          });
        }

        if (accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES) {
          permissionsArray.push({
            facilityId: null,
            permissions: toPermissionPayload(facilityPermissions),
          });
        } else if (applyToAllFacilities) {
          selectedFacilities.forEach((facilityId) => {
            if (toPermissionPayload(facilityPermissions)) {
              permissionsArray.push({
                facilityId: parseInt(facilityId),
                permissions: facilityPermissions,
              });
            }
          });
        } else {
          Object.entries(facilityPermissions).forEach(
            ([facilityId, permissions]) => {
              const validFacilityId =
                facilityId && !isNaN(parseInt(facilityId))
                  ? parseInt(facilityId)
                  : null;
              if (
                validFacilityId &&
                permissions &&
                Object.keys(permissions).length > 0
              ) {
                permissionsArray.push({
                  facilityId: validFacilityId,
                  permissions: permissions,
                });
              }
            },
          );
        }

        userData.permissions = permissionsArray.filter((permission) => {
          if (permission.organizationId) {
            return true;
          }

          if (permission.facilityId === null) {
            return userData.hasAllFacilitiesAccess;
          }

          return (
            permission.facilityId !== null &&
            permission.facilityId !== undefined
          );
        });
      }

      await onSave(userData);
    } catch (err) {
      console.error('Error saving user:', err);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    onClose();
  };

  const handleEmailChange = (event) => {
    handleInputChange('email', event.target.value);
  };

  const handleUserNameChange = (event) => {
    handleInputChange('userName', event.target.value);
  };

  const handleLanguagePreferenceChange = (event) => {
    handleInputChange('languagePreference', event.target.value);
  };

  const handleMfaTypeChange = (event) => {
    handleInputChange('mfa_type', event.target.value);
  };

  const handleOrganizationSelectChange = (event) => {
    handleOrganizationChange(event.target.value);
    if (event.target.value) {
      setFormErrors((prev) => removeErrors(prev, ['organization']));
    }
  };

  const handlePermissionsTypeToggleChange = (event) => {
    handlePermissionsTypeChange(event.target.checked ? 'role' : 'permission');
  };

  const handleApplyToAllFacilitiesToggleChange = (event) => {
    handleApplyToAllFacilitiesChange(event.target.checked);
  };

  const handleOpenOrganizationMirrorDialog = () => {
    openMirrorDialog('organization_permissions');
  };

  const handleOpenFacilityMirrorDialog = () => {
    openMirrorDialog('facility_permissions');
  };

  const hasValidAccessSelection =
    (accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES &&
      selectedFacilities.length > 0) ||
    accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES ||
    (accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS &&
      selectedFacilityGroupIds.length > 0);
  const hasValidPermissionsSelection =
    permissionsType !== 'role' || Boolean(selectedRoleId);
  const isFormValid =
    Object.keys(formErrors).length === 0 &&
    Boolean(formData.email) &&
    Boolean(formData.userName) &&
    Boolean(selectedOrganization) &&
    hasValidPermissionsSelection &&
    hasValidAccessSelection;

  return {
    formData,
    formErrors,
    selectedFacilities,
    selectedOrganization,
    facilityPermissions,
    organizationPermissions,
    applyToAllFacilities,
    selectedFacilityForConfig,
    permissionsType,
    selectedRoleId,
    accessMode,
    selectedFacilityGroupIds,
    roleBasedFacilitiesAccess,
    mirrorDialogOpen,
    mirrorPermissionType,
    applyMirroredPermissions,
    isFormValid,
    getConfiguredFacilitiesCount,
    handleAccessModeChange,
    handleApplyToAllFacilitiesChange,
    handleApplyToAllFacilitiesToggleChange,
    handleApplyTemplate,
    handleClose,
    handleEmailChange,
    handleFacilityPermissionChange,
    handleLanguagePreferenceChange,
    handleMfaTypeChange,
    handleOpenFacilityMirrorDialog,
    handleOpenOrganizationMirrorDialog,
    handleOrganizationChange,
    handleOrganizationPermissionChange,
    handleOrganizationSelectChange,
    handlePermissionsTypeToggleChange,
    handlePermissionsTypeChange,
    handleRoleChange,
    handleSave,
    handleUserNameChange,
    openMirrorDialog,
    closeMirrorDialog,
    setRoleBasedFacilitiesAccess,
    setSelectedFacilities,
    setSelectedFacilityForConfig,
    setSelectedFacilityGroupIds,
  };
};
